/**
 * RequestDetailsModal Component
 *
 * Modal view showing all details for a ticket request.
 * Compact unified card design with competing requests management and action buttons.
 *
 * Created: 2026-01-14
 * Updated: 2026-01-14 - Refactored to compact single-card layout
 */

import React, { useMemo, useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import {
  X,
  Calendar,
  Ticket,
  Users,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  Archive,
  RotateCcw,
  Loader2,
  AlertTriangle,
  Heart,
  Send,
  Mail,
  MailOpen,
  FileText,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Plus,
} from 'lucide-react';
import { useTicketRequestsStore, type TicketRequest } from '../../stores/ticketRequestsStore';
import { useHMCEventsStore, type HMCEvent } from '../../hmcEventsStore';
import { RequestActionButtons } from './RequestActionButtons';
import { EventDetailModal } from '../EventDetailModal';
import { EventSelector } from './EventSelector';

interface RequestDetailsModalProps {
  request: TicketRequest;
  isOpen: boolean;
  onClose: () => void;
  /** Current request index (0-based) */
  currentIndex?: number;
  /** Total number of requests */
  totalRequests?: number;
  /** Callback to navigate to previous request */
  onPrevious?: () => void;
  /** Callback to navigate to next request */
  onNext?: () => void;
}

// Status badge colors
const STATUS_COLORS: Record<TicketRequest['status'], { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
  requested_approval: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Requested Approval' },
  approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Approved' },
  denied: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Denied' },
  waitlist: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Waitlist' },
  fulfilled: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Fulfilled' },
};

// Format date helper - short format
function formatDateShort(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'MMM d');
  } catch {
    return dateStr;
  }
}

// Format date helper - full format
function formatDateFull(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

// Choice status badge
function ChoiceStatusBadge({ status, label }: { status: string | null; label: string }) {
  if (!status) {
    return <span className="text-xs"><span className="text-dark-muted">{label}:</span> <span className="text-dark-text">Not set</span></span>;
  }
  const colors: Record<string, string> = {
    pending: 'text-yellow-400',
    approved: 'text-green-400',
    denied: 'text-red-400',
  };
  return (
    <span className="text-xs">
      <span className="text-dark-muted">{label}:</span>{' '}
      <span className={colors[status] || 'text-dark-text'}>{status}</span>
    </span>
  );
}

// Competing Request Row Component
interface CompetingRequestRowProps {
  competingRequest: TicketRequest;
  isCurrentRequest: boolean;
  onApprove: (requestId: string) => void;
  onDeny: (requestId: string) => void;
  onReset: (requestId: string) => void;
  isUpdating: boolean;
}

function CompetingRequestRow({
  competingRequest,
  isCurrentRequest,
  onApprove,
  onDeny,
  onReset,
  isUpdating,
}: CompetingRequestRowProps) {
  const hasAnyStatus = competingRequest.primary_choice_status !== null;

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
        isCurrentRequest
          ? 'border-purple-500/50 bg-purple-900/20'
          : 'border-dark-border/30 bg-dark-surface/30'
      }`}
    >
      {/* Request Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {isCurrentRequest && (
            <Star className="h-4 w-4 flex-shrink-0 text-purple-400" fill="currentColor" />
          )}
          <span className="font-medium text-white truncate">
            {competingRequest.full_name}
          </span>
          <span className="text-dark-muted text-sm truncate">
            ({competingRequest.company})
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="flex items-center gap-1 text-sm text-purple-300">
            <Ticket className="h-3 w-3" />
            {competingRequest.ticket_count} tix
          </span>
          {competingRequest.primary_choice_status && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                competingRequest.primary_choice_status === 'approved'
                  ? 'bg-green-500/20 text-green-400'
                  : competingRequest.primary_choice_status === 'denied'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}
            >
              {competingRequest.primary_choice_status}
            </span>
          )}
          {competingRequest.special_request && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
              Special
            </span>
          )}
          {competingRequest.wants_benefits_followup && (
            <span className="flex items-center gap-1 text-xs text-pink-400" title="Wants Benefits Follow-up">
              <Heart className="h-3 w-3" />
            </span>
          )}
        </div>
        {isCurrentRequest && (
          <div className="text-xs text-purple-300 mt-1">Current request</div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onApprove(competingRequest.request_id)}
          disabled={isUpdating || competingRequest.primary_choice_status === 'approved'}
          className={`p-2 rounded-lg transition-all duration-150 ${
            competingRequest.primary_choice_status === 'approved'
              ? 'bg-green-600 text-white cursor-default'
              : 'bg-green-500/20 text-green-400 hover:bg-green-500/40 disabled:opacity-50'
          }`}
          title="Approve primary choice"
        >
          {isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
        </button>
        <button
          onClick={() => onDeny(competingRequest.request_id)}
          disabled={isUpdating || competingRequest.primary_choice_status === 'denied'}
          className={`p-2 rounded-lg transition-all duration-150 ${
            competingRequest.primary_choice_status === 'denied'
              ? 'bg-red-600 text-white cursor-default'
              : 'bg-red-500/20 text-red-400 hover:bg-red-500/40 disabled:opacity-50'
          }`}
          title="Deny primary choice"
        >
          {isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
        </button>
        {hasAnyStatus && (
          <button
            onClick={() => onReset(competingRequest.request_id)}
            disabled={isUpdating}
            className="p-2 rounded-lg bg-dark-surface/50/30 text-dark-muted hover:bg-dark-surface/40/50 hover:text-white/90 transition-all duration-150 disabled:opacity-50"
            title="Reset (undo)"
          >
            {isUpdating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// Competing Requests Section Component
interface CompetingRequestsSectionProps {
  request: TicketRequest;
}

function CompetingRequestsSection({ request }: CompetingRequestsSectionProps) {
  const { requests, eventAvailability, updateChoiceStatus, isUpdating } = useTicketRequestsStore();

  // Get all requests for the same event
  const competingRequests = useMemo(() => {
    return requests.filter((r) => r.event_id === request.event_id);
  }, [requests, request.event_id]);

  // Get availability for this event
  const availability = eventAvailability.get(request.event_id);

  // Calculate stats
  const totalTicketsRequested = competingRequests.reduce((sum, r) => sum + r.ticket_count, 0);
  const approvedCount = competingRequests.filter((r) => r.primary_choice_status === 'approved').length;
  const deniedCount = competingRequests.filter((r) => r.primary_choice_status === 'denied').length;
  const pendingCount = competingRequests.filter((r) => r.primary_choice_status === null || r.primary_choice_status === 'pending').length;

  // Sort: current request first, then by status (pending first), then by created_at
  const sortedRequests = useMemo(() => {
    return [...competingRequests].sort((a, b) => {
      // Current request always first
      if (a.request_id === request.request_id) return -1;
      if (b.request_id === request.request_id) return 1;

      // Then by status: pending > denied > approved
      const statusOrder = { pending: 0, null: 0, approved: 2, denied: 1 };
      const aOrder = statusOrder[a.primary_choice_status ?? 'null'] ?? 0;
      const bOrder = statusOrder[b.primary_choice_status ?? 'null'] ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;

      // Then by created_at (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [competingRequests, request.request_id]);

  // Handlers
  const handleApprove = async (requestId: string) => {
    await updateChoiceStatus(requestId, 'approved', undefined, undefined);
  };

  const handleDeny = async (requestId: string) => {
    await updateChoiceStatus(requestId, 'denied', undefined, undefined);
  };

  const handleReset = async (requestId: string) => {
    await updateChoiceStatus(requestId, null, null, undefined);
  };

  if (competingRequests.length <= 1) {
    return null; // Don't show if there's only this request
  }

  return (
    <div className="mt-6 rounded-lg border border-purple-500/30 bg-purple-900/10 p-4">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-white uppercase tracking-wide flex items-center gap-2">
          <Users className="h-4 w-4 text-purple-400" />
          Competing Requests for Primary: {request.event_name}
        </h3>
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-dark-muted">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {competingRequests.length} requests
          </span>
          <span className="flex items-center gap-1">
            <Ticket className="h-3 w-3" />
            {totalTicketsRequested} tickets total
          </span>
          {availability && (
            <span className="flex items-center gap-1">
              <span className={availability.available_tickets > 0 ? 'text-green-400' : 'text-red-400'}>
                {availability.available_tickets} available
              </span>
            </span>
          )}
          <span className="text-green-400">{approvedCount} approved</span>
          <span className="text-red-400">{deniedCount} denied</span>
          <span className="text-yellow-400">{pendingCount} pending</span>
        </div>
      </div>

      {/* Warning if over capacity */}
      {availability && totalTicketsRequested > availability.available_tickets && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-3 text-sm text-yellow-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            Total tickets requested ({totalTicketsRequested}) exceeds available ({availability.available_tickets})
          </span>
        </div>
      )}

      {/* Request List */}
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {sortedRequests.map((competingRequest) => (
          <CompetingRequestRow
            key={competingRequest.request_id}
            competingRequest={competingRequest}
            isCurrentRequest={competingRequest.request_id === request.request_id}
            onApprove={handleApprove}
            onDeny={handleDeny}
            onReset={handleReset}
            isUpdating={isUpdating}
          />
        ))}
      </div>
    </div>
  );
}

// Backup Event Competing Requests Section Component
interface BackupCompetingRequestsSectionProps {
  request: TicketRequest;
  choiceType: 'second' | 'third';
}

function BackupCompetingRequestsSection({ request, choiceType }: BackupCompetingRequestsSectionProps) {
  const { requests, updateChoiceStatus, isUpdating } = useTicketRequestsStore();

  // Get the event name for this choice
  const eventName = choiceType === 'second' ? request.second_choice_event : request.third_choice_event;

  // Get all requests that have this event as their primary, second, or third choice
  const competingRequests = useMemo(() => {
    if (!eventName) return [];
    return requests.filter((r) =>
      r.event_name === eventName ||
      r.second_choice_event === eventName ||
      r.third_choice_event === eventName
    );
  }, [requests, eventName]);

  // Sort: current request first, then by created_at
  const sortedRequests = useMemo(() => {
    return [...competingRequests].sort((a, b) => {
      // Current request always first
      if (a.request_id === request.request_id) return -1;
      if (b.request_id === request.request_id) return 1;
      // Then by created_at (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [competingRequests, request.request_id]);

  // Handlers for backup choice status
  const handleApprove = async (requestId: string) => {
    if (choiceType === 'second') {
      await updateChoiceStatus(requestId, undefined, 'approved', undefined);
    } else {
      await updateChoiceStatus(requestId, undefined, undefined, 'approved');
    }
  };

  const handleDeny = async (requestId: string) => {
    if (choiceType === 'second') {
      await updateChoiceStatus(requestId, undefined, 'denied', undefined);
    } else {
      await updateChoiceStatus(requestId, undefined, undefined, 'denied');
    }
  };

  const handleReset = async (requestId: string) => {
    if (choiceType === 'second') {
      await updateChoiceStatus(requestId, undefined, null, undefined);
    } else {
      await updateChoiceStatus(requestId, undefined, undefined, null);
    }
  };

  if (!eventName || competingRequests.length <= 1) {
    return null; // Don't show if no backup choice or only this request
  }

  const borderColor = choiceType === 'second' ? 'border-brand-500/30' : 'border-cyan-500/30';
  const bgColor = choiceType === 'second' ? 'bg-blue-900/10' : 'bg-cyan-900/10';
  const textColor = choiceType === 'second' ? 'text-blue-400' : 'text-cyan-400';
  const label = choiceType === 'second' ? '2nd Choice' : '3rd Choice';

  return (
    <div className={`mt-4 rounded-lg border ${borderColor} ${bgColor} p-4`}>
      {/* Header */}
      <div className="mb-4">
        <h3 className={`text-sm font-semibold text-white uppercase tracking-wide flex items-center gap-2`}>
          <Users className={`h-4 w-4 ${textColor}`} />
          Competing Requests for {label}: {eventName}
        </h3>
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-dark-muted">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {competingRequests.length} requests
          </span>
        </div>
      </div>

      {/* Request List */}
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {sortedRequests.map((competingRequest) => {
          // Determine which choice this request has for this event
          const isPrimary = competingRequest.event_name === eventName;
          const isSecond = competingRequest.second_choice_event === eventName;
          const isThird = competingRequest.third_choice_event === eventName;
          const choiceLabel = isPrimary ? 'Primary' : isSecond ? '2nd' : isThird ? '3rd' : '';
          const status = isPrimary
            ? competingRequest.primary_choice_status
            : isSecond
              ? competingRequest.backup_choice_status
              : competingRequest.third_choice_status;

          return (
            <div
              key={competingRequest.request_id}
              className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
                competingRequest.request_id === request.request_id
                  ? `${borderColor} ${bgColor}`
                  : 'border-dark-border/30 bg-dark-surface/30'
              }`}
            >
              {/* Request Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {competingRequest.request_id === request.request_id && (
                    <Star className={`h-4 w-4 flex-shrink-0 ${textColor}`} fill="currentColor" />
                  )}
                  <span className="font-medium text-white truncate">
                    {competingRequest.full_name}
                  </span>
                  <span className="text-dark-muted text-sm truncate">
                    ({competingRequest.company})
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`flex items-center gap-1 text-sm ${textColor}`}>
                    <Ticket className="h-3 w-3" />
                    {competingRequest.ticket_count} tix
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full bg-dark-surface/50/30 text-dark-text`}>
                    {choiceLabel}
                  </span>
                  {status && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        status === 'approved'
                          ? 'bg-green-500/20 text-green-400'
                          : status === 'denied'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {status}
                    </span>
                  )}
                </div>
                {competingRequest.request_id === request.request_id && (
                  <div className={`text-xs ${textColor} mt-1`}>Current request</div>
                )}
              </div>

              {/* Action Buttons - only show for the current request's backup choice */}
              {competingRequest.request_id === request.request_id && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleApprove(competingRequest.request_id)}
                    disabled={isUpdating || status === 'approved'}
                    className={`p-2 rounded-lg transition-all duration-150 ${
                      status === 'approved'
                        ? 'bg-green-600 text-white cursor-default'
                        : 'bg-green-500/20 text-green-400 hover:bg-green-500/40 disabled:opacity-50'
                    }`}
                    title={`Approve ${label}`}
                  >
                    {isUpdating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeny(competingRequest.request_id)}
                    disabled={isUpdating || status === 'denied'}
                    className={`p-2 rounded-lg transition-all duration-150 ${
                      status === 'denied'
                        ? 'bg-red-600 text-white cursor-default'
                        : 'bg-red-500/20 text-red-400 hover:bg-red-500/40 disabled:opacity-50'
                    }`}
                    title={`Deny ${label}`}
                  >
                    {isUpdating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                  </button>
                  {status && (
                    <button
                      onClick={() => handleReset(competingRequest.request_id)}
                      disabled={isUpdating}
                      className="p-2 rounded-lg bg-dark-surface/50/30 text-dark-muted hover:bg-dark-surface/40/50 hover:text-white/90 transition-all duration-150 disabled:opacity-50"
                      title="Reset (undo)"
                    >
                      {isUpdating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper to get display name for an event
function getEventDisplayName(event: HMCEvent): string {
  return event.opponent || event.special_event || `Event #${event.id}`;
}

// Compact Info Card - Single unified card with dense layout
interface CompactInfoCardProps {
  request: TicketRequest;
  events: HMCEvent[];
  onEventClick?: () => void;
  onUpdateEventSelections: (
    eventId?: number,
    eventName?: string,
    eventDate?: string,
    eventVenue?: string | null,
    secondChoiceEvent?: string | null,
    thirdChoiceEvent?: string | null
  ) => Promise<void>;
  isUpdating: boolean;
}

function CompactInfoCard({ request, events, onEventClick, onUpdateEventSelections, isUpdating }: CompactInfoCardProps) {
  const statusConfig = STATUS_COLORS[request.status];
  const guestNamesDisplay = request.guest_names?.filter(n => n.trim()).join(', ') || 'None';

  // Edit mode states
  const [isEditingPrimary, setIsEditingPrimary] = useState(false);
  const [isEditingSecond, setIsEditingSecond] = useState(false);
  const [isEditingThird, setIsEditingThird] = useState(false);

  // Filter to only show available events
  const availableEvents = useMemo(() => {
    return events.filter(e => e.is_available !== false);
  }, [events]);

  // Handle primary event change
  const handlePrimaryEventChange = useCallback(async (event: HMCEvent | null) => {
    if (!event) return;
    await onUpdateEventSelections(
      event.id,
      getEventDisplayName(event),
      event.event_date,
      event.venue || null,
      undefined,
      undefined
    );
    setIsEditingPrimary(false);
  }, [onUpdateEventSelections]);

  // Handle backup event changes
  const handleSecondChoiceChange = useCallback(async (event: HMCEvent | null) => {
    await onUpdateEventSelections(
      undefined,
      undefined,
      undefined,
      undefined,
      event ? getEventDisplayName(event) : null,
      undefined
    );
    setIsEditingSecond(false);
  }, [onUpdateEventSelections]);

  const handleThirdChoiceChange = useCallback(async (event: HMCEvent | null) => {
    await onUpdateEventSelections(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      event ? getEventDisplayName(event) : null
    );
    setIsEditingThird(false);
  }, [onUpdateEventSelections]);

  return (
    <div className="rounded-lg border border-dark-border/30 bg-dark-surface/50 divide-y divide-gray-700/50">
      {/* Row 1: Name, Company, Status, Tickets */}
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="font-semibold text-white text-sm truncate">{request.full_name}</span>
            <span className="text-dark-text text-sm">
              {request.company}
              {request.title && <span className="text-dark-muted"> ({request.title})</span>}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
            >
              {statusConfig.label}
            </span>
            {request.is_archived && (
              <span className="flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-400">
                <Archive className="h-3 w-3" />
              </span>
            )}
            <span className="flex items-center gap-1 text-sm font-bold text-purple-400">
              <Ticket className="h-3 w-3" />
              {request.ticket_count} tix
            </span>
          </div>
        </div>
        {/* Contact info */}
        <div className="flex items-center gap-3 mt-1 text-sm">
          <a
            href={`mailto:${request.email}`}
            className="text-purple-400 hover:text-purple-300 hover:underline"
          >
            {request.email}
          </a>
          {request.phone && (
            <>
              <span className="text-dark-muted">|</span>
              <span className="text-white">{request.phone}</span>
            </>
          )}
        </div>
      </div>

      {/* Row 2: Event Info - Editable */}
      <div className="px-4 py-2">
        {/* Primary Event */}
        <div className="mb-2">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-xs font-medium text-dark-muted uppercase tracking-wide">Primary Event</span>
            {!isEditingPrimary && (
              <button
                type="button"
                onClick={() => setIsEditingPrimary(true)}
                className="p-1 rounded hover:bg-dark-input text-dark-muted hover:text-white transition-all duration-150"
                title="Edit primary event"
                disabled={isUpdating}
              >
                <Edit3 className="h-3 w-3" />
              </button>
            )}
          </div>

          {isEditingPrimary ? (
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <EventSelector
                  selectedEvent={{
                    id: request.event_id,
                    name: request.event_name,
                    date: request.event_date,
                    venue: request.event_venue,
                  }}
                  events={availableEvents}
                  onSelect={handlePrimaryEventChange}
                  placeholder="Select primary event..."
                  disabled={isUpdating}
                  compact
                />
              </div>
              <button
                type="button"
                onClick={() => setIsEditingPrimary(false)}
                className="p-1.5 rounded-lg bg-dark-input text-dark-muted hover:text-white transition-all duration-150"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onEventClick}
              className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-purple-500/10 transition-all duration-150 cursor-pointer group"
              title="Click to view event details"
            >
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
                <span className="text-white font-medium group-hover:text-purple-300 transition-all duration-150">
                  {request.event_name}
                </span>
                <span className="text-dark-muted">|</span>
                <span className="text-white">{request.event_date}</span>
                <span className="text-dark-muted">|</span>
                <span className="text-dark-text">{request.event_venue || 'Venue TBD'}</span>
                <ExternalLink className="h-3 w-3 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-auto" />
              </div>
            </button>
          )}
        </div>

        {/* Backup Events Row */}
        <div className="flex gap-4">
          {/* 2nd Choice */}
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-medium text-blue-400/80 uppercase tracking-wide">2nd Choice</span>
              {!isEditingSecond && (
                <button
                  type="button"
                  onClick={() => setIsEditingSecond(true)}
                  className="p-1 rounded hover:bg-dark-input text-dark-muted hover:text-white transition-all duration-150"
                  title={request.second_choice_event ? 'Edit 2nd choice' : 'Add 2nd choice'}
                  disabled={isUpdating}
                >
                  {request.second_choice_event ? <Edit3 className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                </button>
              )}
            </div>

            {isEditingSecond ? (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <EventSelector
                    selectedEvent={request.second_choice_event ? { name: request.second_choice_event } : null}
                    events={availableEvents.filter(e => e.id !== request.event_id)}
                    onSelect={handleSecondChoiceChange}
                    placeholder="Select 2nd choice..."
                    disabled={isUpdating}
                    allowClear
                    compact
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setIsEditingSecond(false)}
                  className="p-1 rounded-lg bg-dark-input text-dark-muted hover:text-white transition-all duration-150"
                  title="Cancel"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="text-sm text-dark-text px-2 py-1">
                {request.second_choice_event || <span className="text-dark-muted italic">None</span>}
              </div>
            )}
          </div>

          {/* 3rd Choice */}
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-medium text-cyan-400/80 uppercase tracking-wide">3rd Choice</span>
              {!isEditingThird && (
                <button
                  type="button"
                  onClick={() => setIsEditingThird(true)}
                  className="p-1 rounded hover:bg-dark-input text-dark-muted hover:text-white transition-all duration-150"
                  title={request.third_choice_event ? 'Edit 3rd choice' : 'Add 3rd choice'}
                  disabled={isUpdating}
                >
                  {request.third_choice_event ? <Edit3 className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                </button>
              )}
            </div>

            {isEditingThird ? (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <EventSelector
                    selectedEvent={request.third_choice_event ? { name: request.third_choice_event } : null}
                    events={availableEvents.filter(e => e.id !== request.event_id && getEventDisplayName(e) !== request.second_choice_event)}
                    onSelect={handleThirdChoiceChange}
                    placeholder="Select 3rd choice..."
                    disabled={isUpdating}
                    allowClear
                    compact
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setIsEditingThird(false)}
                  className="p-1 rounded-lg bg-dark-input text-dark-muted hover:text-white transition-all duration-150"
                  title="Cancel"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="text-sm text-dark-text px-2 py-1">
                {request.third_choice_event || <span className="text-dark-muted italic">None</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Guests & Flags */}
      <div className="px-4 py-2">
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3 text-dark-muted" />
            <span className="text-dark-muted">Guests:</span>
            <span className="text-white">{guestNamesDisplay}</span>
          </span>
          <span className="text-dark-muted">|</span>
          <span className="flex items-center gap-1">
            <FileText className="h-3 w-3 text-dark-muted" />
            <span className="text-dark-muted">Special:</span>
            {request.special_request ? (
              <span className="text-yellow-400 font-medium" title={request.special_request_note || 'Yes'}>Yes</span>
            ) : (
              <span className="text-dark-text">No</span>
            )}
          </span>
          <span className="text-dark-muted">|</span>
          <span className="flex items-center gap-1">
            <Heart className="h-3 w-3 text-dark-muted" />
            <span className="text-dark-muted">Benefits F/U:</span>
            <span className={request.wants_benefits_followup ? 'text-green-400 font-medium' : 'text-dark-text'}>
              {request.wants_benefits_followup ? 'Yes' : 'No'}
            </span>
          </span>
          <span className="text-dark-muted">|</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-dark-muted" />
            <span className="text-dark-muted">Standby:</span>
            <span className={request.is_standby_request ? 'text-yellow-400 font-medium' : 'text-dark-text'}>
              {request.is_standby_request ? 'Yes' : 'No'}
            </span>
          </span>
        </div>
      </div>

      {/* Row 4: Status & Email Tracking */}
      <div className="px-4 py-2">
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <ChoiceStatusBadge status={request.primary_choice_status} label="Primary" />
          <span className="text-dark-muted">|</span>
          <ChoiceStatusBadge status={request.backup_choice_status} label="2nd" />
          {request.third_choice_event && (
            <>
              <span className="text-dark-muted">|</span>
              <ChoiceStatusBadge status={request.third_choice_status} label="3rd" />
            </>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs">
          <span className="flex items-center gap-1">
            <Send className="h-3 w-3 text-dark-muted" />
            <span className="text-dark-muted">Email:</span>
            <span className="text-dark-text">{request.email_sent_at ? `Sent ${formatDateShort(request.email_sent_at)}` : 'Not sent'}</span>
          </span>
          <span className="text-dark-muted">|</span>
          <span className="flex items-center gap-1">
            <Mail className="h-3 w-3 text-dark-muted" />
            <span className="text-dark-muted">Delivered:</span>
            <span className="text-dark-text">{formatDateShort(request.email_delivered_at)}</span>
          </span>
          <span className="text-dark-muted">|</span>
          <span className="flex items-center gap-1">
            <MailOpen className={`h-3 w-3 ${request.email_opened_at ? 'text-green-400' : 'text-dark-muted'}`} />
            <span className="text-dark-muted">Opened:</span>
            <span className={request.email_opened_at ? 'text-green-400 font-medium' : 'text-dark-text'}>
              {formatDateShort(request.email_opened_at)}
            </span>
          </span>
        </div>
        {/* Created timestamp */}
        <div className="mt-1 text-xs text-dark-muted">
          Created: {formatDateFull(request.created_at)}
        </div>
      </div>

      {/* Row 5: Special Request Note (if exists) */}
      {request.special_request && request.special_request_note && (
        <div className="px-4 py-2">
          <div className="flex items-start gap-2 text-xs">
            <FileText className="h-3 w-3 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-dark-muted">Special Request: </span>
              <span className="text-yellow-200">{request.special_request_note}</span>
            </div>
          </div>
        </div>
      )}

      {/* Row 6: Status Notes (if exists) */}
      {request.status_notes && (
        <div className="px-4 py-2">
          <div className="flex items-start gap-2 text-xs">
            <Star className="h-3 w-3 text-dark-muted flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-dark-muted">Notes: </span>
              <span className="text-white">{request.status_notes}</span>
            </div>
          </div>
        </div>
      )}

      {/* Row 7: Archive Info (if archived) */}
      {request.is_archived && request.archived_at && (
        <div className="px-4 py-2 bg-amber-500/5">
          <div className="flex items-center gap-2 text-xs text-amber-400">
            <Archive className="h-3 w-3" />
            <span>Archived: {formatDateFull(request.archived_at)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
  request,
  isOpen,
  onClose,
  currentIndex,
  totalRequests,
  onPrevious,
  onNext,
}) => {
  const [showEventDetails, setShowEventDetails] = useState(false);
  const { events } = useHMCEventsStore();
  const { updateEventSelections, isUpdating } = useTicketRequestsStore();

  // Navigation state
  const hasNavigation = currentIndex !== undefined && totalRequests !== undefined && totalRequests > 0;
  const canGoPrevious = hasNavigation && currentIndex > 0;
  const canGoNext = hasNavigation && currentIndex < totalRequests - 1;

  // Find the event data by event_id
  const eventData = useMemo(() => {
    return events.find((e) => e.id === request.event_id);
  }, [events, request.event_id]);

  // Handle event click - open event details side panel
  const handleEventClick = () => {
    if (eventData) {
      setShowEventDetails(true);
    }
  };

  // Handle event selections update
  const handleUpdateEventSelections = useCallback(async (
    eventId?: number,
    eventName?: string,
    eventDate?: string,
    eventVenue?: string | null,
    secondChoiceEvent?: string | null,
    thirdChoiceEvent?: string | null
  ) => {
    await updateEventSelections(
      request.request_id,
      eventId,
      eventName,
      eventDate,
      eventVenue,
      secondChoiceEvent,
      thirdChoiceEvent
    );
  }, [request.request_id, updateEventSelections]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-dark-border/30 bg-dark-bg shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-dark-border/30 bg-dark-surface/50 px-4 py-3">
          <h2 id="modal-title" className="text-base font-semibold text-white">
            Request Details
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-dark-muted hover:bg-dark-input hover:text-white transition-all duration-150"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Compact Info Card */}
          <CompactInfoCard
            request={request}
            events={events}
            onEventClick={handleEventClick}
            onUpdateEventSelections={handleUpdateEventSelections}
            isUpdating={isUpdating}
          />

          {/* Competing Requests Section - shows all requests for same event */}
          <CompetingRequestsSection request={request} />

          {/* Backup Event Competing Requests Sections */}
          {request.second_choice_event && (
            <BackupCompetingRequestsSection request={request} choiceType="second" />
          )}
          {request.third_choice_event && (
            <BackupCompetingRequestsSection request={request} choiceType="third" />
          )}

          {/* Action Buttons Section */}
          <div className="mt-4">
            <h3 className="mb-3 text-xs font-semibold text-dark-muted uppercase tracking-wide">
              Actions
            </h3>
            <RequestActionButtons request={request} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-dark-border/30 bg-dark-surface/50 px-4 py-3">
          {/* Navigation Buttons */}
          <div className="flex items-center gap-2">
            {hasNavigation && (
              <>
                <button
                  onClick={onPrevious}
                  disabled={!canGoPrevious}
                  className="flex items-center gap-1 rounded-lg bg-dark-input px-3 py-1.5 text-sm font-medium text-white hover:bg-dark-surface/40 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous request"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <span className="text-sm text-dark-muted px-2">
                  {currentIndex + 1} / {totalRequests}
                </span>
                <button
                  onClick={onNext}
                  disabled={!canGoNext}
                  className="flex items-center gap-1 rounded-lg bg-dark-input px-3 py-1.5 text-sm font-medium text-white hover:bg-dark-surface/40 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next request"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="rounded-lg bg-dark-input px-3 py-1.5 text-sm font-medium text-white hover:bg-dark-surface/40 transition-all duration-150"
          >
            Close
          </button>
        </div>
      </div>

      {/* Event Details Side Panel */}
      {eventData && (
        <EventDetailModal
          event={eventData}
          isOpen={showEventDetails}
          onClose={() => setShowEventDetails(false)}
          allEvents={events}
        />
      )}
    </div>
  );
};

export default RequestDetailsModal;
